import { useState, useCallback } from 'react';
import { useAuth } from './useAuth';

export function useHistory() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const apiCall = useCallback(async (url, options = {}) => {
    const token = localStorage.getItem('authToken');

    // Build headers - include auth token if available
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    // Add auth header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // For non-GET requests, require authentication
    const method = options.method || 'GET';
    if (!token && method !== 'GET') {
      throw new Error('Authentication required');
    }

    const response = await fetch(url, {
      ...options,
      headers
    });

    // Check if response is ok before parsing JSON
    if (!response.ok) {
      // Try to parse as JSON for error details, but handle non-JSON responses (like HTML error pages)
      let errorMessage = `Request failed with status ${response.status}`;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        try {
          const data = await response.json();
          errorMessage = data.error || errorMessage;
        } catch (e) {
          // JSON parsing failed, keep the default error message
          console.error('[useHistory] Failed to parse error response as JSON:', e);
        }
      } else {
        // Non-JSON response (likely HTML error page)
        console.error('[useHistory] Received non-JSON response:', contentType);
      }

      throw new Error(errorMessage);
    }

    // Response is ok, parse as JSON
    const data = await response.json();
    return data;
  }, []);

  const getTranscripts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const transcripts = await apiCall('/api/transcripts');
      return transcripts;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [apiCall]);

  const getTranscript = useCallback(async (transcriptId) => {
    setIsLoading(true);
    setError(null);

    try {
      console.log('[DEBUG] useHistory.getTranscript called with ID:', {
        id: transcriptId,
        idLength: transcriptId?.length,
        lastChar: transcriptId?.charAt(transcriptId?.length - 1),
        lastCharCode: transcriptId?.charCodeAt(transcriptId?.length - 1)
      });
      const transcript = await apiCall(`/api/transcripts/${transcriptId}`);
      console.log('[DEBUG] useHistory.getTranscript response:', {
        hasData: !!transcript?.data,
        id: transcript?.id
      });
      return transcript;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [apiCall]);

  const updateTranscript = useCallback(async (transcriptId, updates) => {
    setError(null);
    
    try {
      const transcript = await apiCall(`/api/transcripts/${transcriptId}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
      return transcript;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }, [apiCall]);

  const deleteTranscript = useCallback(async (transcriptId) => {
    setError(null);
    
    try {
      await apiCall(`/api/transcripts/${transcriptId}`, {
        method: 'DELETE'
      });
      return true;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }, [apiCall]);

  const getVersionHistory = useCallback(async (transcriptId) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const versions = await apiCall(`/api/transcripts/${transcriptId}/versions`);
      return versions;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [apiCall]);

  const saveVersion = useCallback(async (transcriptId, versionData) => {
    setError(null);
    
    try {
      const version = await apiCall(`/api/transcripts/${transcriptId}/versions`, {
        method: 'POST',
        body: JSON.stringify(versionData)
      });
      return version;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }, [apiCall]);

  const restoreVersion = useCallback(async (transcriptId, versionId) => {
    setError(null);
    
    try {
      const newVersion = await apiCall(`/api/transcripts/${transcriptId}/restore/${versionId}`, {
        method: 'POST'
      });
      return newVersion;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }, [apiCall]);

  const searchTranscripts = useCallback(async (query, filters = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (query) params.append('q', query);
      if (filters.tags && filters.tags.length > 0) {
        params.append('tags', filters.tags.join(','));
      }
      if (filters.folderId) params.append('folderId', filters.folderId);

      const results = await apiCall(`/api/search?${params.toString()}`);
      return results;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [apiCall]);

  const createTranscriptFromVersion = useCallback(async (transcriptId, versionId) => {
    setError(null);

    try {
      const newTranscript = await apiCall(`/api/transcripts/${transcriptId}/versions/${versionId}/create-transcript`, {
        method: 'POST'
      });
      return newTranscript;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }, [apiCall]);

  const cloneTranscript = useCallback(async (transcriptId) => {
    setError(null);

    try {
      const clonedTranscript = await apiCall(`/api/transcripts/${transcriptId}/clone`, {
        method: 'POST'
      });
      return clonedTranscript;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }, [apiCall]);

  const claimGuestTranscript = useCallback(async (transcriptId) => {
    setError(null);

    try {
      const claimedTranscript = await apiCall(`/api/transcripts/${transcriptId}/claim`, {
        method: 'POST'
      });
      return claimedTranscript;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }, [apiCall]);

  return {
    isLoading,
    error,
    getTranscripts,
    getTranscript,
    updateTranscript,
    deleteTranscript,
    getVersionHistory,
    saveVersion,
    restoreVersion,
    searchTranscripts,
    createTranscriptFromVersion,
    cloneTranscript,
    claimGuestTranscript,
    clearError: () => setError(null)
  };
}